import { buildLocaleProperties } from '~/assets/js/helpers/analytics'
import buildProductEvents from './track/product'
import buildCartEvents from './track/cart'
import buildCouponEvents from './track/coupon'
import buildReferralEvents from './track/referral'
import buildExperimentEvents from './track/experiment'
import buildExploreEvents from './track/explore'
import buildLensGuideEvents from './track/lens-guide'
import buildAttributionEvents from './track/attribution'
import buildNavigationEvents from './track/navigation'
import buildMultiStepEvents from './track/multi-step'
import buildGlobalEvents from './track/global'
import buildFormEvents from './track/form'
import buildFilterEvents from './track/filters'
import buildEmailSignupEvents from './track/email-signup'
import buildTaxesAndDutiesEvents from './track/tax-popup'
import buildContentEvents from './track/content'
import buildWelcomeEvents from './track/welcome'
import buildSupportEvents from './track/support'
import buildCompetitionEvents from './track/competition'
import buildInvestEvents from './track/invest'
import buildConvincerEvents from './track/convincers'
import buildGiftSelectorEvents from './track/gift-selector'
import buildUpsellsEvents from './track/upsells'
import buildQuickAddEvents from './track/quick-add'
import buildBundleEvents from './track/bundles'

import getFBQ from './track/fbq'
import pinterest from './track/pinterest'
import reddit from './track/reddit'
import tiktok from './track/tiktok'
import bing from './track/bing'
import googleAds from './track/google-ads'
import ga4 from './track/ga4'
import * as amplitude from '@amplitude/analytics-browser'

// Only 5% of sessions are sampled for replay, but the SDK was previously imported
// statically and shipped to 100% of users. Decide membership up front so the (large)
// plugin bundle is only downloaded for sampled sessions.
const SESSION_REPLAY_SAMPLE_RATE = 0.05

export default defineNuxtPlugin((nuxtApp) => {
  try {
    const route = useRoute()
    const router = useRouter()
    const geoCountry = useGeoCountry()
    const userStore = useUserStore()
    const runtimeConfig = useRuntimeConfig()

    // Create and Install Session Replay Plugin for sampled sessions only.
    // Pre-sampled above, so the plugin itself runs at sampleRate 1 for loaded sessions.
    if (Math.random() < SESSION_REPLAY_SAMPLE_RATE) {
      import('@amplitude/plugin-session-replay-browser')
        .then(({ sessionReplayPlugin }) => {
          amplitude.add(
            sessionReplayPlugin({
              forceSessionTracking: true, // Enable capture of Session Start and Session End events
              sampleRate: 1, // session already sampled at import time
            })
          )
        })
        .catch((error) => {
          nuxtApp.$sentry?.captureException(error)
        })
    }

    amplitude.init(runtimeConfig.public.amplitude.apiKey, {
      defaultTracking: {
        attribution: true,
        pageViews: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
      },
    })

    const fbq = getFBQ(nuxtApp, amplitude?.getDeviceId())

    router.afterEach(() => {
      const unlocalisedPath =
        route.meta.unlocalisedPath || window.location.pathname.replace(/^\/[a-z]+-([a-z]+)\//, '/') // strip out the locale from location path
      const storefrontLocale = route.params.locale || 'en-gb'
      const contentLocale = nuxtApp.$i18n?.localeProperties?.value?.contentLocale || 'en-gb'

      amplitude?.track(`Viewed ${route.meta.pageType || route.name}`, {
        pagePath: route.path,
        pageFullPath: route.fullPath,
        pageName: route.name,
        pageType: route.meta.pageType,
        ...buildLocaleProperties(storefrontLocale, contentLocale, geoCountry.value),
        unlocalisedPath,
      })

      fbq?.track('PageView')
      pinterest?.pageVisit()
      reddit.pageVisit()
      tiktok?.pageVisit()
      bing?.pageVisit()
      googleAds?.pageVisit()
      ga4?.page(route.meta.pageType || route.name)
    })

    // Create centralized tracking dispatcher
    const trackingDispatcher = {
      track: (event, properties) => {
        const enrichedProperties = {
          ...properties,
          pagePath: route.path,
          pageFullPath: route.fullPath,
          pageName: route.name,
          pageType: route.meta.pageType,
        }

        // Send to Amplitude
        amplitude.track(event, enrichedProperties)

        // Send to GA4
        ga4.track(event, enrichedProperties)

        return Promise.resolve()
      },

      page: (name, properties) => {
        const enrichedProperties = {
          ...properties,
          pagePath: route.path,
          pageFullPath: route.fullPath,
          pageName: route.name,
          pageType: route.meta.pageType,
        }

        amplitude.track(`Page Viewed${name ? ` - ${name}` : ''}`, enrichedProperties)
        ga4.page(name, enrichedProperties)

        return Promise.resolve()
      },

      identify: (userId, traits) => {
        if (userId || traits) {
          const identifyEvent = new amplitude.Identify()
          if (userId) identifyEvent.set('user_id', userId)
          if (traits) {
            Object.keys(traits).forEach((key) => {
              identifyEvent.set(key, traits[key])
            })
          }
          amplitude.identify(identifyEvent)

          // Send to GA4
          ga4.identify(userId, traits)
        }

        return Promise.resolve()
      },

      getAnonymousId: () => {
        // For backwards compatibility, return amplitude device id
        return amplitude.getDeviceId() || null
      },
    }

    // Add experiment tracking as user properties
    const abTestStore = useAbTestStore()
    const tests = abTestStore.tests
    const identifyEvent = new amplitude.Identify()

    for (const key in tests) {
      identifyEvent.set(`experiment-${key}`, tests[key].value)
    }

    amplitude.identify(identifyEvent)

    trackingDispatcher.product = buildProductEvents(trackingDispatcher, fbq, googleAds, ga4)
    trackingDispatcher.cart = buildCartEvents(trackingDispatcher)
    trackingDispatcher.coupon = buildCouponEvents(trackingDispatcher)
    trackingDispatcher.referral = buildReferralEvents(trackingDispatcher)
    trackingDispatcher.experiment = buildExperimentEvents(trackingDispatcher)
    trackingDispatcher.explore = buildExploreEvents(trackingDispatcher)
    trackingDispatcher.lensGuide = buildLensGuideEvents(trackingDispatcher)
    trackingDispatcher.attribution = buildAttributionEvents(trackingDispatcher)
    trackingDispatcher.navigation = buildNavigationEvents(trackingDispatcher)
    trackingDispatcher.multiStep = buildMultiStepEvents(trackingDispatcher)
    trackingDispatcher.global = buildGlobalEvents(trackingDispatcher)
    trackingDispatcher.form = buildFormEvents(trackingDispatcher, fbq)
    trackingDispatcher.filter = buildFilterEvents(trackingDispatcher)
    trackingDispatcher.emailSignup = buildEmailSignupEvents(trackingDispatcher)
    trackingDispatcher.content = buildContentEvents(trackingDispatcher)
    trackingDispatcher.welcome = buildWelcomeEvents(trackingDispatcher)
    trackingDispatcher.support = buildSupportEvents(trackingDispatcher)
    trackingDispatcher.competition = buildCompetitionEvents(trackingDispatcher)
    trackingDispatcher.invest = buildInvestEvents(trackingDispatcher)
    trackingDispatcher.convincers = buildConvincerEvents(trackingDispatcher)
    trackingDispatcher.giftSelector = buildGiftSelectorEvents(trackingDispatcher)
    trackingDispatcher.taxPopup = buildTaxesAndDutiesEvents(trackingDispatcher)
    trackingDispatcher.upsells = buildUpsellsEvents(trackingDispatcher)
    trackingDispatcher.quickAdd = buildQuickAddEvents(trackingDispatcher)
    trackingDispatcher.bundles = buildBundleEvents(trackingDispatcher)

    trackingDispatcher.updateUserData = () => {
      fbq.setUserDataFromContext(trackingDispatcher.getAnonymousId())
      fbq.init()

      if (userStore.email) {
        const identifyEvent = new amplitude.Identify()
        identifyEvent.set('user_id', userStore.email)
        amplitude.identify(identifyEvent)
      }
    }

    return {
      provide: {
        track: trackingDispatcher,
        fbq: fbq,
        amplitude,
        tiktok,
        bing,
        googleAds,
        ga4,
      },
    }
  } catch (error) {
    // This isn't a critical error that should break the site.
    const { $sentry } = nuxtApp
    if ($sentry) {
      $sentry.captureException(error)
    } else {
      console.error(error)
    }

    const proxy = new Proxy(
      {},
      {
        get:
          () =>
          (...args) => {
            console.error(...args)
          },
      }
    )
    const track = new Proxy(proxy, {
      get: (_, prop) => {
        if (prop === 'identify' || prop === 'getAnonymousId' || prop === 'updateUserData') {
          return () => {
            console.error(`Tracking not available: ${prop}`)
          }
        }
        console.error('Tracking not available:', prop)
        return proxy
      },
    })

    return {
      provide: {
        track,
        fbq: proxy,
        amplitude,
        tiktok: proxy,
        bing: proxy,
        googleAds: proxy,
        ga4: proxy,
      },
    }
  }
})
